/**
 * WHY an exchange refused a credential, as a stable key — never as its own words.
 *
 * One classifier for both sides of the wire: the backend answers the «Connect API»
 * path with the kind's sentence (the raw library text never leaves the server) and
 * the customer-side «Test credentials» button names the same fix from the exchange's
 * own message. They read the same third-party strings, so they live in one place.
 */

export type ExchangeCredentialFailureKind =
  | 'invalidKey'
  | 'permissions'
  | 'ipNotAllowed'
  | 'clockSkew'
  | 'testnetUnsupported'
  | 'unknown'

/**
 * Classify the exchange's own message. Substrings, case-insensitive, because
 * every exchange words it differently:
 *   Kraken   `EAPI:Invalid key` · `EGeneral:Permission denied` · `EAPI:Invalid nonce`
 *   Binance  `API-key format invalid` · `Invalid API-key, IP, or permissions for action`
 *   Coinbase `Invalid API Key` · `Unauthorized`
 *
 * Order matters: Binance names key, IP and permissions in ONE sentence, and the
 * actionable advice there is the permissions one (the key itself parsed fine).
 */
export function classifyExchangeCredentialFailure(
  rawError: string | undefined | null,
): ExchangeCredentialFailureKind {
  const text = (rawError ?? '').toLowerCase()
  if (text === '') return 'unknown'

  // FIRST: not the exchange talking at all — the adapter refuses before any
  // request leaves us when the config carries no `testnetUrl`. Falling through to
  // `invalidKey` would send the user to regenerate a key that was never looked at.
  if (text.includes('testnet') || text.includes('sandbox')) return 'testnetUnsupported'
  if (text.includes('nonce') || text.includes('timestamp') || text.includes('recvwindow')) {
    return 'clockSkew'
  }
  if (
    text.includes('permission') ||
    text.includes('unauthorized') ||
    text.includes('not authorized')
  ) {
    return 'permissions'
  }
  if (text.includes('ip address') || text.includes('ip whitelist') || text.includes('whitelist')) {
    return 'ipNotAllowed'
  }
  if (text.includes('invalid key') || text.includes('invalid api') || text.includes('key format')) {
    return 'invalidKey'
  }
  return 'unknown'
}
